import {
  Box,
  Typography,
  InputAdornment,
  TextField,
  Button,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Add from "@mui/icons-material/Add";
import { Search, Clear } from "@mui/icons-material";
import HomeOutlined from "@mui/icons-material/HomeOutlined";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material";
import { ModulePath } from "../../../constant/ApplicationVariables";
import { useNavigate } from "react-router-dom";

const RegressionHeader = ({
  onNewSuiteClick,
  hasSuites,
  searchTerm,
  onSearchChange,
  viewMode = "grid",
  onViewModeChange,
  totalPlans = 0,
  filteredCount = 0,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleViewChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      onViewModeChange?.(newViewMode);
    }
  };

  const handleClearSearch = () => {
    onSearchChange("");
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        mb: 3,
      }}
    >
      {/* Main Header Section */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        {/* Left side - Home button and title */}
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            className="flex items-center cursor-pointer rounded p-1"
            onClick={() => navigate(ModulePath.projects)}
            sx={{
              background: "hsl(var(--cyan))",
              "&:hover": { opacity: 0.85 },
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              transition: "all 0.2s ease",
            }}
          >
            <HomeOutlined
              fontSize="small"
              sx={{ color: "hsl(var(--primary-foreground))" }}
            />
          </Box>
        </Box>

        {/* Right side - All buttons and search */}
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          {/* Search TextField */}
          <TextField
            placeholder="Search plans..."
            size="small"
            disabled={!hasSuites && !searchTerm}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ 
              width: isMobile ? 200 : 260,
              '& .MuiOutlinedInput-root': {
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    edge="end"
                    sx={{ 
                      p: 0.5,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                height: 40,
                borderRadius: 2,
              },
            }}
          />

          {/* View Toggle Buttons - Always enabled */}
          {/* <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewChange}
            aria-label="view mode"
            size="small"
            sx={{
              height: 40,
              '& .MuiToggleButton-root': {
                px: 2,
                py: 1,
                borderColor: theme.palette.divider,
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  },
                },
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              },
            }}
          >
            <ToggleButton value="grid" aria-label="grid view">
              <ViewModuleIcon sx={{ fontSize: 20 }} />
              {!isMobile && (
                <Typography variant="caption" sx={{ ml: 0.5, display: { xs: 'none', md: 'block' } }}>
                  Grid
                </Typography>
              )}
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon sx={{ fontSize: 20 }} />
              {!isMobile && (
                <Typography variant="caption" sx={{ ml: 0.5, display: { xs: 'none', md: 'block' } }}>
                  List
                </Typography>
              )}
            </ToggleButton>
          </ToggleButtonGroup> */}

          {/* New Plan Button */}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={onNewSuiteClick}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              height: 40,
              px: isMobile ? 2 : 3,
              background: "linear-gradient(90deg, hsl(var(--cyan)), hsl(var(--emerald)))",
              color: "hsl(var(--primary-foreground))",
              boxShadow: "0 4px 14px hsl(var(--cyan) / 0.35)",
              transition: "all 0.2s ease",
              whiteSpace: "nowrap",
              "&:hover": {
                background: "linear-gradient(90deg, hsl(var(--cyan) / 0.9), hsl(var(--emerald) / 0.9))",
                boxShadow: "0 6px 20px hsl(var(--cyan) / 0.45)",
                transform: "translateY(-1px)",
              },
              "&:active": {
                transform: "scale(0.98)",
              },
            }}
          >
            {isMobile ? "New" : "New Plan"}
          </Button>
        </Box>
      </Box>

      {/* Stats Bar - Show when there are plans */}
      {hasSuites && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1,
            px: 1.5,
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            borderRadius: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="caption" color="text.secondary">
              Total: {totalPlans}
            </Typography>
            {searchTerm && (
              <Typography variant="caption" color="primary">
                Filtered: {filteredCount}
              </Typography>
            )}
          </Box>
          {searchTerm && (
            <Button
              size="small"
              onClick={handleClearSearch}
              sx={{
                textTransform: "none",
                fontSize: "0.7rem",
                minWidth: "auto",
                px: 1,
              }}
            >
              Clear search
            </Button>
          )}
        </Box>
      )}

      {/* Visual separation */}
      <Divider sx={{ mt: 0 }} />
    </Box>
  );
};

export default RegressionHeader;